
import React, { useState } from 'react';

export default function ServicesHero() {
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const scrollToSection = (id: string) => {
    try {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      } else {
        console.warn(`Element with id "${id}" not found.`);
      }
    } catch (error) {
      console.error('Failed to scroll to section:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();

    formData.forEach((value, key) => {
      if (typeof value === 'string') {
        data.append(key, value);
      } else {
        data.append(key, String(value));
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6g6tjqau1b90a404c2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      setSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
      // Keep the form visible but inform the user of the failure
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img
            src="https://public.readdy.ai/ai/img_res/5ccfecf4597df086f8f5b615fbd338c6.jpg"
            alt="Residential roofing services across Pennsylvania"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-matte-black via-matte-black/85 to-matte-black/50"></div>
        </div>

        <div className="relative z-10 w-full px-8 lg:px-16 py-32 pt-64">
          <div className="max-w-7xl mx-auto">
            <p className="text-xs uppercase tracking-[0.3em] mb-6 font-semibold text-neon-green">
              What We Do Best
            </p>
            <h1 className="mb-6 leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
              <span className="block text-6xl lg:text-[5.5rem] text-white tracking-wide">
                Our Expert
              </span>
              <span className="block text-6xl lg:text-[5.5rem] text-neon-green tracking-wide">
                Services
              </span>
            </h1>
            <p
              className="text-lg lg:text-xl leading-relaxed max-w-2xl mb-10 text-white/80 tracking-wide"
              style={{ fontFamily: "'Oswald', sans-serif", fontWeight: 300 }}
            >
              From complete roof replacements to storm damage restoration, IronCore Roofing &amp; Exteriors delivers premium craftsmanship and lasting protection for your
              home.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => scrollToSection('roofing-section')}
                className="bg-neon-green text-matte-black px-8 py-3 rounded-lg font-semibold text-sm flex items-center gap-2 hover:bg-white transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Explore Services
                <i className="ri-arrow-down-line text-lg"></i>
              </button>
              <button
                onClick={() => {
                  setShowQuoteForm(true);
                  setSubmitted(false);
                }}
                className="border border-white/30 text-white px-8 py-3 rounded-lg font-semibold text-sm flex items-center gap-2 hover:border-neon-green hover:text-neon-green transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Get Free Estimate
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Modal */}
      {showQuoteForm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowQuoteForm(false);
          }}
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg relative overflow-hidden">
            {/* Header */}
            <div className="bg-matte-black px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-lg">
                  Get Your FREE Quote
                </h2>
                <p className="text-gray-400 text-xs mt-0.5">
                  We&apos;ll get back to you within 24 hours
                </p>
              </div>
              <button
                onClick={() => setShowQuoteForm(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white transition-colors cursor-pointer rounded-full hover:bg-white/10"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-5">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                    <i className="ri-check-line text-3xl text-green-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800 mb-2">
                    Request Submitted!
                  </h3>
                  <p className="text-gray-500 text-sm mb-6">
                    Thank you! We&apos;ll be in touch shortly to discuss your project.
                  </p>
                  <button
                    onClick={() => setShowQuoteForm(false)}
                    className="bg-neon-green text-matte-black px-6 py-2 rounded-md font-bold text-sm hover:bg-green-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form
                  id="services-hero-quote-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Full Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="full_name"
                        required
                        placeholder="John Smith"
                        className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-neon-green transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Phone Number <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        placeholder="(717) 000-0000"
                        className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-neon-green transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="john@example.com"
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-neon-green transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Property Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="address"
                      required
                      placeholder="123 Main St, City, PA"
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-neon-green transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1">
                      Work Requested <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="work_description"
                      required
                      maxLength={500}
                      rows={3}
                      placeholder="Describe the work you need done (e.g., roof replacement, siding repair, gutters...)..."
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-neon-green transition-colors resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-neon-green text-matte-black py-2.5 rounded-md font-bold text-sm hover:bg-green-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                  >
                    {submitting ? 'Submitting...' : 'Submit FREE Quote Request'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
